import * as THREE from 'three'

import { artworkBaseIndex } from './vertice'

/**
 * Gets an array of artwork UVs.
 */
const getArtworkUvs = (
  top: number,
  right: number,
  bottom: number,
  left: number,
) => {
  return [
    [
      [
        new THREE.Vector2(left, top),
        new THREE.Vector2(right, bottom),
        new THREE.Vector2(right, top),
      ],
      [
        new THREE.Vector2(left, top),
        new THREE.Vector2(left, bottom),
        new THREE.Vector2(right, bottom),
      ],
    ],
  ]
}

/**
 * Gets an array of artwork faces.
 * All faces are laid out in ring fashion, following CSS TRBL order.
 */
const getArtworkFaces = () => {
  // Frame faces, positioned by face rings
  // All faces are laid out in ring fashion, following CSS TRBL order
  return [
    // Front-facing
    [artworkBaseIndex + 0, artworkBaseIndex + 2, artworkBaseIndex + 1],
    [artworkBaseIndex + 0, artworkBaseIndex + 3, artworkBaseIndex + 2],
  ].map(
    (face) =>
      new THREE.Face3(
        face[0] - artworkBaseIndex,
        face[1] - artworkBaseIndex,
        face[2] - artworkBaseIndex,
      ),
  )
}

/**
 * Generates artwork geometry.
 */
export const generateArtworkGeometry = (
  artworkGeometry: THREE.Geometry,
  vertices: THREE.Vector3[],
) => {
  artworkGeometry.vertices.push(
    ...vertices.slice(artworkBaseIndex, artworkBaseIndex + 4),
  )
  artworkGeometry.verticesNeedUpdate = true

  const artworkFaces = getArtworkFaces()

  artworkGeometry.faces.push(...artworkFaces)
  artworkGeometry.computeFaceNormals()
}

/**
 * Updates artwork geometry.
 */
export const updateArtworkGeometry = (
  artworkGeometry: THREE.Geometry,
  vertices: THREE.Vector3[],
) => {
  vertices
    .slice(artworkBaseIndex, artworkBaseIndex + 4)
    .forEach((vertice, index) => {
      artworkGeometry.vertices[index].set(vertice.x, vertice.y, vertice.z)
    })

  artworkGeometry.verticesNeedUpdate = true
  artworkGeometry.elementsNeedUpdate = true
  artworkGeometry.computeFaceNormals()
}

/**
 * Generates artwork UVs.
 */
export const generateArtworkUvs = (
  artworkGeometry: THREE.Geometry,
  artworkWidth: number,
  artworkHeight: number,
) => {
  const aspectRatio = artworkWidth / artworkHeight
  const adjustedWidth = 1 * aspectRatio
  const adjustedHeight = 1 / aspectRatio

  let top = 1
  let right = 1
  let bottom = 0
  let left = 0

  // Crop horizontal
  if (aspectRatio < 1) {
    const stretchDifference = adjustedWidth - 1
    const stretchWidth = stretchDifference / 2

    right = 1 + stretchWidth
    left = 0 - stretchWidth
  }

  // Crop vertical
  if (aspectRatio > 1) {
    const stretchDifference = adjustedHeight - 1
    const stretchHeight = stretchDifference / 2

    top = 1 + stretchHeight
    bottom = 0 - stretchHeight
  }

  artworkGeometry.faceVertexUvs = getArtworkUvs(top, right, bottom, left)
  artworkGeometry.uvsNeedUpdate = true
  artworkGeometry.elementsNeedUpdate = true
}

/**
 * Updates artwork UVs.
 */
export const updateArtworkUvs = (
  artworkGeometry: THREE.Geometry,
  artworkWidth: number,
  artworkHeight: number,
) => {
  const aspectRatio = artworkWidth / artworkHeight
  const adjustedWidth = 1 * aspectRatio
  const adjustedHeight = 1 / aspectRatio

  let top = 1
  let right = 1
  let bottom = 0
  let left = 0

  // Crop horizontal
  if (aspectRatio < 1) {
    const stretchDifference = adjustedWidth - 1
    const stretchWidth = stretchDifference / 2

    right = 1 + stretchWidth
    left = 0 - stretchWidth
  }

  // Crop vertical
  if (aspectRatio > 1) {
    const stretchDifference = adjustedHeight - 1
    const stretchHeight = stretchDifference / 2

    top = 1 + stretchHeight
    bottom = 0 - stretchHeight
  }

  artworkGeometry.faceVertexUvs[0][0][0].set(left, top)
  artworkGeometry.faceVertexUvs[0][0][1].set(right, bottom)
  artworkGeometry.faceVertexUvs[0][0][2].set(right, top)

  artworkGeometry.faceVertexUvs[0][1][0].set(left, top)
  artworkGeometry.faceVertexUvs[0][1][1].set(left, bottom)
  artworkGeometry.faceVertexUvs[0][1][2].set(right, bottom)

  artworkGeometry.uvsNeedUpdate = true
}
